/**
 * Sistema RBAC da V2 — fonte única da verdade para autorização.
 *
 * Hierarquia: viewer < user < admin
 * - viewer: apenas leitura (consulta, filtros, dashboards e relatórios)
 * - user:   operacional completo (criar/editar/excluir lançamentos, técnicos, proformas, importações)
 *           — NÃO acessa gestão de usuários nem operações administrativas críticas
 * - admin:  acesso total (inclui gestão de usuários, alteração de roles, gestão de regras financeiras
 *           configuráveis como retention_rules)
 *
 * Esta matriz é usada tanto no backend (procedures/middleware) quanto no frontend
 * (UI guards e ocultação de menus).
 *
 * Diferenças vs V1:
 * - RBAC agora é escopado por empresa (via `company_memberships`); cada usuário tem N
 *   memberships (1 por empresa) com role por empresa.
 * - Novas permissões: `canManageFinancialRules` (gestão de retention_rules etc.) e
 *   `canEditAppliedFinancials` (edição manual de snapshots aplicados).
 *
 * Role `financial_manager` é OPCIONAL e fica reservada para o futuro (4ª role).
 * No MVP só temos 3 roles.
 */

export const ROLES = ["technician", "manager", "client", "viewer", "user", "admin"] as const;
export type Role = (typeof ROLES)[number];

export const ROLE_LABELS: Record<Role, string> = {
  technician: "Técnico (portal)",
  manager: "Gerente de projeto (chantier)",
  client: "Cliente (portal)",
  viewer: "Visualizador",
  user: "Usuário",
  admin: "Administrador",
};

export const ROLE_DESCRIPTIONS: Record<Role, string> = {
  technician:
    "Portal do técnico: acessa SÓ o portal (/portal) — busca o carro pela placa e assume o serviço. Nenhum acesso ao ERP.",
  manager:
    "Portal do chantier (/chantier): recepção e entrega do veículo (check-lists, danos, franquia), vê o devis completo mas NUNCA comissões. Nenhum acesso ao ERP.",
  client:
    "Portal do cliente (/cliente): vê a agenda do chantier dele e agenda os próprios veículos (dia/horário). Nenhum acesso ao ERP.",
  viewer:
    "Apenas leitura: pode pesquisar, filtrar e visualizar dashboards e relatórios. Não cria, edita ou exclui registros.",
  user: "Operacional: pode criar, editar e excluir vehicle_service_jobs, técnicos, recebimentos, pagamentos e proformas. Não gerencia usuários nem altera regras financeiras.",
  admin:
    "Acesso total: tudo do nível Usuário + gestão de usuários, alteração de roles, gestão de retention_rules e edição de snapshots aplicados.",
};

/**
 * Hierarquia numérica para comparações rápidas.
 * F-tech-portal: `technician` fica ABAIXO de viewer (rank 0) — falha TODO
 * gate `hasMinimumRole(..., "viewer"|"user"|"admin")` por construção; o
 * portal usa um procedure próprio que exige exatamente este papel.
 */
const ROLE_RANK: Record<Role, number> = {
  technician: 0,
  manager: 0,
  client: 0,
  viewer: 1,
  user: 2,
  admin: 3,
};

export function isRole(value: unknown): value is Role {
  return typeof value === "string" && (ROLES as readonly string[]).includes(value);
}

/** Normaliza qualquer string vinda do banco/JWT para um Role válido. Default: viewer (mais restritivo). */
export function normalizeRole(value: unknown): Role {
  return isRole(value) ? value : "viewer";
}

/** Retorna true se `role` está no nível `min` ou superior na hierarquia. */
export function hasMinimumRole(role: Role | undefined | null, min: Role): boolean {
  if (!role) return false;
  return ROLE_RANK[role] >= ROLE_RANK[min];
}

// ─── Matriz de permissões ─────────────────────────────────────────────────────

export interface Permissions {
  /** Pode visualizar páginas operacionais (dashboards, listas, filtros, relatórios). */
  canView: boolean;
  /** Pode criar vehicle_service_jobs, técnicos, proformas e importar dados. */
  canCreate: boolean;
  /** Pode editar registros existentes (jobs, técnicos, recebimentos, pagamentos). */
  canEdit: boolean;
  /** Pode excluir registros. */
  canDelete: boolean;
  /** Pode acessar a área de gestão de usuários. */
  canManageUsers: boolean;
  /** Pode alterar permissões/roles de outros usuários. */
  canChangeUserRoles: boolean;
  /** Pode executar operações administrativas críticas (recálculo global, força mudança operational_company). */
  canRunAdminOps: boolean;
  /** Pode acessar páginas administrativas (recalcular, configurações de sistema). */
  canViewAdmin: boolean;
  /** Pode gerenciar regras financeiras configuráveis (retention_rules, commission_rules futuras). */
  canManageFinancialRules: boolean;
  /** Pode editar manualmente snapshots aplicados em jobs/invoices (com reason + audit log). */
  canEditAppliedFinancials: boolean;
}

export function getPermissions(role: Role | undefined | null): Permissions {
  const r = normalizeRole(role);
  switch (r) {
    case "technician":
    case "manager":
    case "client":
      // Portais apenas — nenhum acesso às páginas do ERP.
      return {
        canView: false,
        canCreate: false,
        canEdit: false,
        canDelete: false,
        canManageUsers: false,
        canChangeUserRoles: false,
        canRunAdminOps: false,
        canViewAdmin: false,
        canManageFinancialRules: false,
        canEditAppliedFinancials: false,
      };
    case "admin":
      return {
        canView: true,
        canCreate: true,
        canEdit: true,
        canDelete: true,
        canManageUsers: true,
        canChangeUserRoles: true,
        canRunAdminOps: true,
        canViewAdmin: true,
        canManageFinancialRules: true,
        canEditAppliedFinancials: true,
      };
    case "user":
      return {
        canView: true,
        canCreate: true,
        canEdit: true,
        canDelete: true,
        canManageUsers: false,
        canChangeUserRoles: false,
        canRunAdminOps: false,
        canViewAdmin: false,
        canManageFinancialRules: false,
        canEditAppliedFinancials: false,
      };
    case "viewer":
    default:
      return {
        canView: true,
        canCreate: false,
        canEdit: false,
        canDelete: false,
        canManageUsers: false,
        canChangeUserRoles: false,
        canRunAdminOps: false,
        canViewAdmin: false,
        canManageFinancialRules: false,
        canEditAppliedFinancials: false,
      };
  }
}

// ─── F-acesso-por-menu (CEO 2026-10-12) ───────────────────────────────────────
/**
 * Menus do Gestion que podem ser liberados, um a um, para um usuário de
 * PORTAL (gerente de chantier). Ids = `id` dos itens do ErpShell. O servidor
 * mapeia cada router a um menu (apps/api/src/menu-grants.ts); o recorte dos
 * dados (só os carros dos chantiers que ele gerencia) é aplicado nas listas.
 */
export const ERP_MENUS = [
  "painel",
  "dossiers",
  "devis",
  "clientes",
  "chantiers",
  "agenda",
  "veiculos",
  "tecnicos",
  "faturas",
] as const;
export type ErpMenu = (typeof ERP_MENUS)[number];
export const MENU_GRANT_LEVELS = ["view", "operate"] as const;
export type MenuGrantLevel = (typeof MENU_GRANT_LEVELS)[number];
export interface MenuGrant {
  companyId: number;
  menu: ErpMenu;
  level: MenuGrantLevel;
}
export function isErpMenu(value: unknown): value is ErpMenu {
  return typeof value === "string" && (ERP_MENUS as readonly string[]).includes(value);
}
/** Rota de cada menu liberável (para o guard de rota e o ErpShell). */
export const ERP_MENU_PATHS: Record<ErpMenu, string> = {
  painel: "/dashboard",
  dossiers: "/dossiers",
  devis: "/devis",
  clientes: "/clientes",
  chantiers: "/chantiers",
  agenda: "/agenda",
  veiculos: "/veiculos",
  tecnicos: "/tecnicos",
  faturas: "/faturas",
};
/** Menu dono de um pathname do ERP (prefixo), ou null se não é menu liberável. */
export function erpMenuForPath(pathname: string): ErpMenu | null {
  for (const m of ERP_MENUS) {
    const p = ERP_MENU_PATHS[m];
    if (pathname === p || pathname.startsWith(p + "/")) return m;
  }
  return null;
}
/**
 * Usuário que entra no Gestion SÓ por menus liberados: nenhum papel de ERP
 * (viewer/user/admin) e pelo menos um grant ativo numa empresa onde ele tem
 * papel de portal.
 */
export function hasOnlyMenuGrantAccess(user: {
  isSuperAdmin: boolean;
  memberships: Array<{ active: boolean; role: string; companyId: number }>;
  menuGrants?: MenuGrant[] | undefined;
}): boolean {
  if (user.isSuperAdmin) return false;
  const active = user.memberships.filter((m) => m.active);
  if (active.some((m) => hasMinimumRole(normalizeRole(m.role), "viewer"))) return false;
  return (user.menuGrants ?? []).some((g) => active.some((m) => m.companyId === g.companyId));
}

/** Mensagens padronizadas para erros de autorização. */
export const PERMISSION_MESSAGES = {
  authRequired: "Faça login para continuar.",
  readOnly: "Permissão somente leitura. Solicite ao administrador para editar.",
  adminOnly: "Acesso restrito ao administrador.",
  cannotChangeOwnRole: "Você não pode alterar a sua própria permissão.",
  cannotDeleteSelf: "Você não pode excluir a sua própria conta.",
  financialRulesAdminOnly:
    "Apenas administradores podem gerenciar regras financeiras (retenções, comissões padrão).",
  appliedFinancialsAdminOnly:
    "Apenas administradores podem editar valores aplicados em lançamentos. Justificativa obrigatória.",
} as const;
